import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';

import {
  getLeaveReconciliations,
  getLeaveReconciliationPdf,
  regenerateLeaveReconciliation,
  deliverLeaveReconciliation,
  revokeLeaveReconciliation,
} from '../../../services/leaveReconciliationService';
import PersonCell from '../../../components/ui/PersonCell';
import { formatDate } from '../../../utils/Utils';

// StatusBadge/statusColors deseni payroll-summary/config'ten AYNALANDI — bu depoda
// draft/generated/delivered gibi küçük, sabit bir durum kümesini renklendirmenin
// yerleşik yolu bu (bkz. src/pages/payroll-summary/config/index.js).
const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $bg }) => $bg || '#9e9e9e15'};
  color: ${({ $color }) => $color || '#9e9e9e'};
`;

// Bulgu 6 uyarı rozeti — STATUS_COLORS'ın `generated` turuncusuyla AYNI aile,
// ama uyarı kırmızısı: "durum ilerledi" değil, "elindeki kâğıt bayat olabilir".
const StaleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: #e5393515;
  color: #e53935;
  cursor: help;
`;

const STATUS_COLORS = {
  draft: { bg: '#9e9e9e15', text: '#9e9e9e' },
  generated: { bg: '#ff980015', text: '#ff9800' },
  delivered: { bg: '#4caf5015', text: '#4caf50' },
};

const useLeaveReconciliationsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [scopeError, setScopeError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getLeaveReconciliations(paramsToUse)
      .then((res) => {
        // api.js GET okumaları reject ETMEZ — gerçek sinyal res.data.status (bkz.
        // reference_api_interceptor_never_rejects_reads). Modül kapalıyken ya da
        // kapsam dışındayken backend 403 döner (LeaveScopeResolver) — LeaveBalanceReportPage
        // ile AYNI desen: satır İÇİ bir açıklama, boş tablo DEĞİL.
        //
        // M-3 (final inceleme): `message` OLMADAN gelen bir başarısızlık
        // "Size bağlı personel bulunamadı." diye GÖSTERİLMEZ. Servis artık
        // `passErrorBody: true` geçtiği için modül/kapsam/yıl hataları GERÇEK
        // anahtarıyla gelir; anahtar YOKSA (500, ağ kopması → EMPTY_RESPONSE)
        // bilinmeyen bir hatayı "personeliniz yok" diye anlatmak yerine
        // "liste yüklenemedi" denir. `noScope`u yalnız backend söylerse yazarız.
        if (!res.data?.status) {
          const key = res.data?.message;
          setScopeError(key ? `responseErrors.${key}` : 'leaveReconciliation.loadFailed');
          setData([]);
          setPagination({ totalRowCount: 0, pageIndex: 0, pageSize: paramsToUse.pageSize || 10 });
          return;
        }
        setScopeError(null);
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // PDF üretimi/indirme — backend `GetOrCreateAsync` ile draft bir satırı da
  // (hiç üretilmemiş) ilk indirmede `generated`e taşır; bu yüzden başarılı bir
  // indirmeden sonra listeyi TAZELERİZ — aksi halde satırın rozeti "Oluşturulmadı"
  // yazmaya devam eder ve "Teslim Al" aksiyonu (yalnız generated'ta görünür) hiç
  // çıkmaz (bkz. MyLeavesPage.handleDownloadForm — AYNI blob/content-type deseni).
  const downloadPdf = useCallback(async (userId, year) => {
    try {
      const res = await getLeaveReconciliationPdf(userId, year);
      const contentType = res.data?.type || res.headers?.['content-type'] || '';
      if (!contentType.includes('pdf')) return false;
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `izin-mutabakat-${year}-${String(userId).slice(0, 8).toUpperCase()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Bulgu G — dökümü BİLEREK tazele. İndirme (downloadPdf) donmuş dökümü
  // basar ve onu DEĞİŞTİRMEZ; bu eylem donmayı açıkça kırar ve yeni PDF'i
  // aynı anda indirir. `delivered` satırda backend reddeder (buton da
  // gösterilmez). Blob/content-type deseni downloadPdf ile BİREBİR.
  const regenerate = useCallback(async (userId, year) => {
    try {
      const res = await regenerateLeaveReconciliation(userId, year);
      const contentType = res.data?.type || res.headers?.['content-type'] || '';
      if (!contentType.includes('pdf')) return false;
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `izin-mutabakat-${year}-${String(userId).slice(0, 8).toUpperCase()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const deliver = useCallback(async (userId, year, formData) => {
    try {
      await deliverLeaveReconciliation(userId, year, {
        signedDocumentUrl: formData.signedDocumentUrl,
        note: formData.note || null,
      });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const revoke = useCallback(async (userId, year, formData) => {
    try {
      await revokeLeaveReconciliation(userId, year, formData.note);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const statusLabels = useMemo(() => ({
    draft: t('leaveReconciliation.status.draft'),
    generated: t('leaveReconciliation.status.generated'),
    delivered: t('leaveReconciliation.status.delivered'),
  }), [t]);

  // Sunucu tarafı filtre/sayfalama: year/status araç çubuğundaki (tablo İÇİNDEKİ)
  // seçicilerden gelir, bu yüzden hiçbir kolon kendi filtresini TAŞIMAZ —
  // DataTable'ın kendi filtre diyaloğu `column.name` üretir ve backend'in
  // gerçek sorgu anahtarlarıyla (userId, departmentId...) hiç eşleşmez (bkz.
  // reference_datatable_filterkey_dead) — LeaveBalanceReportPage'in `disableFilters`
  // kararıyla AYNI gerekçe. Sıralama da yok: List ucu orderBy/orderDir almıyor.
  const columns = useMemo(() => [
    {
      name: 'fullName',
      label: t('leaveReconciliation.employee'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => <PersonCell name={v} />,
      },
    },
    {
      name: 'departmentName',
      label: t('leaveReconciliation.department'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'titleName',
      label: t('leaveReconciliation.jobTitle'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'year',
      label: t('leaveReconciliation.year'),
      options: { filter: false, sort: false },
    },
    {
      name: 'usedDays',
      label: t('leaveReconciliation.usedDays'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ?? 0) },
    },
    {
      name: 'status',
      label: t('leaveReconciliation.statusLabel'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => {
          const colors = STATUS_COLORS[v] || STATUS_COLORS.draft;
          return <StatusBadge $bg={colors.bg} $color={colors.text}>{statusLabels[v] || v}</StatusBadge>;
        },
      },
    },
    {
      name: 'deliveredAt',
      label: t('leaveReconciliation.deliveredAt'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? formatDate(v, lang) : '-'),
      },
    },
    // Toplu bildirimle çalışana "formun hazır" e-postası gittiği an (kuyruğa yazıldığı
    // an — gönderim sağlayıcısı birkaç saniye sonra teslim eder). Boşsa kişiye hiç
    // bildirim gitmemiştir; toplu bildirim varsayılan olarak doluları ATLAR.
    {
      name: 'notifiedAt',
      label: t('leaveReconciliation.notifiedAt'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v ? formatDate(v, lang) : '-'),
      },
    },
    // DÜZELTME TURU 4, BULGU 6 — ÇALIŞANIN KENDİ TAZELEMESİ İK'YA GÖRÜNÜR.
    // Islak imza `generated` ile `delivered` ARASINDA atılır: İK basar, çalışan
    // kâğıdı imzalar, İK sonra "Teslim Al"a basar. O pencerede çalışanın kendi
    // "Formu Yeniden Üret" düğmesi açıktır ve dökümü yeniden yazar; teslim
    // ardından YENİ izin kümesi `signed` damgalanır. Eylem kapatılmadı (tur 2'nin
    // G bulgusu onu açmak için vardı) — ama İK'nın hiçbir sinyali yoktu: durum
    // `generated` kalıyor, yalnız sunucu tarafı nota bir satır ekleniyordu.
    // Kolon doluysa: elinizdeki imzalı kopya kayıtla uyuşmuyor olabilir.
    {
      name: 'employeeRegeneratedAt',
      label: t('leaveReconciliation.employeeRegenerated'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v
          ? (
            <StaleBadge title={t('leaveReconciliation.employeeRegeneratedHint')}>
              <AlertTriangle size={12} /> {formatDate(v, lang)}
            </StaleBadge>
          )
          : '-'),
      },
    },
  ], [t, lang, statusLabels]);

  return {
    fetching, columns, data, pagination, scopeError,
    refresh, downloadPdf, regenerate, deliver, revoke, statusLabels,
  };
};

export default useLeaveReconciliationsApi;
